import React, { useState, useRef, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import {
  FileSpreadsheet, Download, Upload, CheckCircle2, AlertTriangle, ArrowRight,
  Layers, PlusCircle, GitBranch, Loader2, Asterisk,
} from "lucide-react";

const money = (n) => (n == null ? "—" : "$" + Number(n).toLocaleString(undefined, { maximumFractionDigits: 0 }));

export default function EstimateIO() {
  const navigate = useNavigate();
  const [busy, setBusy] = useState(false);
  const [plan, setPlan] = useState(null);          // preview from /estimates/import/parse
  const fileRef = useRef(null);
  const [bids, setBids] = useState([]);
  const [revTarget, setRevTarget] = useState("");  // bid_id chosen to revise, "" = new bid / auto
  const [conf, setConf] = useState({});            // {role: {alias} | {base, fringe}} for unmapped classifications
  const [params] = useSearchParams();
  const pending = params.get("import");

  useEffect(() => { api.get("/bids").then((r) => setBids((r.data || []).slice(0, 200))).catch(() => {}); }, []);
  useEffect(() => {
    if (!pending) { return; }
    api.get(`/estimates/import/${pending}`).then((r) => setPlan(r.data))
      .catch((e) => toast.error(e.response?.data?.detail || "This import preview isn't available"));
  }, [pending]);

  const blobDownload = (data, filename) => {
    const url = URL.createObjectURL(data);
    const a = document.createElement("a"); a.href = url; a.download = filename; a.click();
    URL.revokeObjectURL(url);
  };

  const downloadTemplate = async () => {
    try {
      const res = await api.get("/estimates/template", { responseType: "blob" });
      blobDownload(res.data, "gridforge_estimate_workbook.xlsx");
      toast.success("Workbook downloaded — fill it in and upload it back");
    } catch { toast.error("Could not download the workbook"); }
  };

  const onFile = async (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    setPlan(null); setBusy(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      const res = await api.post("/estimates/import/parse", fd);
      setPlan(res.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not read that workbook");
    } finally { setBusy(false); if (fileRef.current) fileRef.current.value = ""; }
  };

  const commit = async () => {
    if (!plan?.token) return;
    const unmapped = plan.unmapped_roles || [];
    const missing = unmapped.filter((r) => {
      const c = conf[r]; return !c || (!c.alias && (c.base == null || c.base === ""));
    });
    if (missing.length) return toast.error(`Map every classification first: ${missing.join(", ")}`);
    setBusy(true);
    try {
      const res = await api.post("/estimates/import/commit", {
        token: plan.token,
        target_bid_id: revTarget || undefined,
        as_revision: revTarget ? true : undefined,
        conformance_map: unmapped.length ? conf : undefined,
      });
      toast.success(res.data.mode === "update" ? "Estimate imported as a new revision" : "Estimate imported into a new bid");
      setRevTarget(""); setConf({});
      navigate(`/bids/${res.data.bid_id}?tab=estimating`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Import failed");
    } finally { setBusy(false); }
  };

  const nl = plan?.new_libraries || {};
  const newLibTotal = ["labor_roles", "equipment", "crews", "task_types"].reduce((s, k) => s + (nl[k]?.length || 0), 0);

  return (
    <div className="max-w-[1800px] w-full mx-auto px-6 sm:px-8 py-8" data-testid="estimate-io-page">
      <h1 className="font-display text-2xl font-bold tracking-tight text-black flex items-center gap-2">
        <FileSpreadsheet className="w-5 h-5 text-[#003E87]" /> Estimate import &amp; export
      </h1>
      <p className="text-sm text-slate-500 mt-0.5 mb-6 max-w-2xl">
        One workbook is the whole estimate — every input in, every number out. Download it, fill in the project, scope,
        libraries, General Conditions and clauses on their own tabs, and upload it back to a fully working bid. The
        locked <b>Reference</b> tab lists every allowed value (statuses, disciplines, live commodity indexes, cost codes)
        so nothing is guessed. Missing libraries are created for you; sent bids come back as a new revision.
      </p>

      <Card className="p-5 mb-4">
        <div className="flex flex-wrap items-center gap-3">
          <Button variant="outline" onClick={downloadTemplate} data-testid="est-download-template">
            <Download className="w-4 h-4 mr-1.5" /> Download workbook
          </Button>
          <label className="inline-flex items-center gap-2 h-9 px-3 rounded-md bg-[#003E87] text-white text-sm hover:bg-[#00305F] cursor-pointer" data-testid="est-upload-label">
            {busy && !plan ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
            {busy && !plan ? "Reading…" : "Import filled workbook"}
            <input ref={fileRef} type="file" accept=".xlsx" className="hidden" onChange={onFile} disabled={busy} data-testid="est-upload-input" />
          </label>
          <span className="text-xs text-slate-400">To export a bid, open it and use <b>Export estimate</b> in the header.</span>
        </div>
        <div className="mt-3 grid grid-cols-1 sm:grid-cols-3 gap-2 text-xs text-slate-500">
          <div className="flex items-start gap-1.5"><Layers className="w-3.5 h-3.5 text-[#003E87] mt-0.5" /> Project, scope, libraries, General Conditions, weather &amp; mobilization — all in one file</div>
          <div className="flex items-start gap-1.5"><CheckCircle2 className="w-3.5 h-3.5 text-emerald-500 mt-0.5" /> Preview line count &amp; totals before anything is written</div>
          <div className="flex items-start gap-1.5"><GitBranch className="w-3.5 h-3.5 text-[#003E87] mt-0.5" /> Round-trip a full export to update the same bid</div>
        </div>
      </Card>

      {plan && (
        <Card className="p-5" data-testid="est-plan">
          {plan.via === "claude" && (
            <div className="mb-4 flex items-start gap-2 rounded-lg border border-[#D97757]/30 bg-[#D97757]/5 px-3 py-2 text-xs text-slate-700" data-testid="est-claude-banner">
              <Asterisk className="w-4 h-4 text-[#D97757] shrink-0 mt-0.5" />
              Claude prepared this import from a workbook. Nothing has been written yet. Check the numbers below, then confirm.
            </div>
          )}
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              {plan.mode === "update" ? <GitBranch className="w-5 h-5 text-[#003E87]" /> : <PlusCircle className="w-5 h-5 text-emerald-600" />}
              <div>
                <div className="text-sm font-semibold text-slate-800" data-testid="est-plan-title">
                  {plan.mode === "update"
                    ? (plan.is_revision ? `Update “${plan.target_bid_name}” as a NEW revision` : `Update existing bid “${plan.target_bid_name}”`)
                    : `Create new estimate: “${plan.project_name}”`}
                </div>
                <div className="text-xs text-slate-400">{plan.line_count} scope line{plan.line_count === 1 ? "" : "s"} · status: {plan.status}</div>
              </div>
            </div>
            {plan.is_revision && <Badge className="bg-amber-50 text-amber-700 hover:bg-amber-50">Sent bid → new revision</Badge>}
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-4">
            {[
              ["Direct cost", money(plan.totals?.direct_cost)],
              ["General Conditions", money(plan.totals?.general_conditions)],
              ["Total", money(plan.totals?.total)],
              ["$ / Watt", plan.totals?.cost_per_watt != null ? `$${plan.totals.cost_per_watt}/W` : "—"],
            ].map(([k, v]) => (
              <div key={k} className="rounded-lg border border-slate-100 px-3 py-2">
                <div className="text-[11px] uppercase tracking-wide text-slate-400">{k}</div>
                <div className="text-base font-semibold text-slate-800 font-mono" data-testid={`est-total-${k.replace(/[^a-z]/gi, "").toLowerCase()}`}>{v}</div>
              </div>
            ))}
          </div>

          {plan.diff && (plan.diff.added.length + plan.diff.changed.length + plan.diff.removed.length) > 0 && (
            <div className="mb-4 rounded-lg border border-slate-100 p-3" data-testid="est-diff">
              <div className="text-xs font-semibold text-slate-600 mb-2">What changes vs the current bid</div>
              <div className="flex flex-wrap gap-3 text-[11px] mb-2">
                <span className="text-emerald-600">+{plan.diff.added.length} added</span>
                <span className="text-[#8a6d00]">~{plan.diff.changed.length} changed</span>
                <span className="text-rose-600">−{plan.diff.removed.length} removed</span>
              </div>
              <ul className="max-h-40 overflow-y-auto text-[11px] text-slate-500 space-y-0.5">
                {plan.diff.added.map((n, i) => <li key={`a${i}`} className="text-emerald-700">+ {n}</li>)}
                {plan.diff.changed.map((c, i) => <li key={`c${i}`} className="text-[#8a6d00]">~ {c.name} — {c.fields.map((f) => f.replace(/_override|_pct/g, "").replace(/_/g, " ")).join(", ")}</li>)}
                {plan.diff.removed.map((n, i) => <li key={`r${i}`} className="text-rose-600">− {n}</li>)}
              </ul>
            </div>
          )}

          {newLibTotal > 0 && (
            <div className="mb-4 text-xs" data-testid="est-new-libraries">
              <div className="text-slate-500 mb-1 flex items-center gap-1.5"><PlusCircle className="w-3.5 h-3.5 text-[#003E87]" /> These library items will be created for you:</div>
              <div className="flex flex-wrap gap-1.5">
                {["task_types", "crews", "labor_roles", "equipment"].flatMap((k) =>
                  (nl[k] || []).map((n) => <span key={k + n} className="px-2 py-0.5 rounded bg-[#003E87]/8 text-[#003E87]">{n}</span>)
                )}
              </div>
            </div>
          )}

          {(plan.warnings || []).length > 0 && (
            <div className="mb-4" data-testid="est-warnings">
              <div className="flex items-center gap-1.5 text-xs text-amber-700 mb-1"><AlertTriangle className="w-3.5 h-3.5" /> {plan.warnings.length} note{plan.warnings.length === 1 ? "" : "s"} — these lines won't price until fixed:</div>
              <ul className="max-h-40 overflow-y-auto text-[11px] text-slate-500 space-y-0.5 list-disc pl-5">
                {plan.warnings.map((w, i) => <li key={i}>{w}</li>)}
              </ul>
            </div>
          )}

          {/* Unmapped classifications — must conform before pricing */}
          {(plan.unmapped_roles || []).length > 0 && (
            <div className="mb-4 rounded-lg border border-rose-200 bg-rose-50/50 p-3" data-testid="est-conformance">
              <div className="text-xs font-semibold text-rose-700 mb-2 flex items-center gap-1.5">
                <AlertTriangle className="w-3.5 h-3.5" /> {plan.unmapped_roles.length} classification(s) have no rate — map each to price this bid
              </div>
              <div className="space-y-2">
                {plan.unmapped_roles.map((role) => {
                  const c = conf[role] || {};
                  const setC = (patch) => setConf((p) => ({ ...p, [role]: { ...(p[role] || {}), ...patch } }));
                  return (
                    <div key={role} className="flex flex-wrap items-center gap-2 text-xs" data-testid={`est-conf-${role}`}>
                      <span className="font-medium text-slate-700 w-48 truncate">{role}</span>
                      <Select value={c.alias || "__manual"} onValueChange={(v) => setC(v === "__manual" ? { alias: "" } : { alias: v, base: "", fringe: "" })}>
                        <SelectTrigger className="w-56 h-8" data-testid={`est-conf-select-${role}`}><SelectValue placeholder="Conform to…" /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="__manual">Manual base + fringe…</SelectItem>
                          {(plan.labor_roles || []).map((r) => <SelectItem key={r} value={r}>Use rate of: {r}</SelectItem>)}
                        </SelectContent>
                      </Select>
                      {!c.alias && (
                        <>
                          <Input className="w-24 h-8" placeholder="Base $/hr" value={c.base ?? ""} onChange={(e) => setC({ base: e.target.value })} data-testid={`est-conf-base-${role}`} />
                          <Input className="w-24 h-8" placeholder="Fringe $/hr" value={c.fringe ?? ""} onChange={(e) => setC({ fringe: e.target.value })} data-testid={`est-conf-fringe-${role}`} />
                        </>
                      )}
                    </div>
                  );
                })}
              </div>
              <div className="text-[11px] text-slate-500 mt-2">These mappings are saved to the active rate library so future imports conform automatically.</div>
            </div>
          )}

          {/* Import as a revision of an existing bid */}
          <div className="mb-4 rounded-lg border border-slate-100 p-3" data-testid="est-revision-target">
            <div className="text-xs font-semibold text-slate-600 mb-1.5 flex items-center gap-1.5">
              <GitBranch className="w-3.5 h-3.5 text-[#003E87]" /> Import these numbers as a revision?
            </div>
            <Select value={revTarget || "none"} onValueChange={(v) => setRevTarget(v === "none" ? "" : v)}>
              <SelectTrigger className="w-full" data-testid="est-revision-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="none">No — {plan.mode === "update" ? "follow the workbook (update / auto-revision)" : "create a brand-new bid"}</SelectItem>
                {bids.map((bd) => (
                  <SelectItem key={bd.bid_id} value={bd.bid_id} data-testid={`est-revision-opt-${bd.bid_id}`}>
                    New revision of: {bd.name}{bd.client_name ? ` — ${bd.client_name}` : ""}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {revTarget && <div className="text-[11px] text-amber-700 mt-1.5">The current estimate on that bid is sealed as history; these numbers become the new active revision (starts fresh).</div>}
          </div>

          <div className="flex items-center justify-end gap-2 border-t border-slate-100 pt-4">
            <Button variant="outline" onClick={() => { setPlan(null); setRevTarget(""); }} disabled={busy} data-testid="est-cancel-import">Cancel</Button>
            <Button onClick={commit} disabled={busy} className="bg-[#F4C400] hover:bg-[#D9AE00] text-black font-semibold" data-testid="est-confirm-import">
              {busy ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <ArrowRight className="w-4 h-4 mr-1.5" />}
              {revTarget ? "Bring in as revision" : (plan.mode === "update" ? (plan.is_revision ? "Bring in as revision" : "Apply update") : "Create estimate")}
            </Button>
          </div>
        </Card>
      )}
    </div>
  );
}
